import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from '../Context/TranslationContext';
import { Award, Target, Users, Medal, BookOpen, CheckSquare, Calendar, Clock } from 'lucide-react';

const RashtrapatiBadgePage = () => {
  const { isTamil } = useTranslation();

  const translations = {
    title: {
      en: "Rashtrapati Scout/Guide Award",
      ta: "ராஷ்ட்ரபதி சாரணர்/சாரணியர் விருது"
    },
    subtitle: {
      en: "The Highest Recognition in Bharat Scouts and Guides",
      ta: "பாரத சாரணர் மற்றும் சாரணியர் இயக்கத்தின் உயரிய அங்கீகாரம்"
    },
    description: {
      en: "The Rashtrapati Scout/Guide Award is the highest recognition that can be achieved in the Bharat Scouts and Guides movement. It represents excellence in scouting skills, leadership, and service to the community.",
      ta: "ராஷ்ட்ரபதி சாரணர்/சாரணியர் விருது என்பது பாரத சாரணர் மற்றும் சாரணியர் இயக்கத்தில் பெறக்கூடிய மிக உயர்ந்த அங்கீகாரமாகும். இது சாரணர் திறன்கள், தலைமைத்துவம் மற்றும் சமூக சேவையில் சிறப்பை குறிக்கிறது."
    },
    sections: {
      eligibility: {
        title: {
          en: "Eligibility Criteria",
          ta: "தகுதி அளவுகோல்கள்"
        },
        content: {
          en: [
            "Must have earned the Rajya Puraskar Award",
            "Age between 15-25 years",
            "Minimum of 2 years active service after Rajya Puraskar",
            "Regular attendance in Unit/Group activities",
            "Demonstrated leadership qualities",
            "Good academic record and character"
          ],
          ta: [
            "ராஜ்ய புரஸ்கார் விருது பெற்றிருக்க வேண்டும்",
            "வயது 15-25 ஆண்டுகளுக்கு இடையில் இருக்க வேண்டும்",
            "ராஜ்ய புரஸ்காருக்குப் பிறகு குறைந்தபட்சம் 2 ஆண்டுகள் செயலில் சேவை",
            "அலகு/குழு செயல்பாடுகளில் வழக்கமான வருகை",
            "தலைமைத்துவ பண்புகளை வெளிப்படுத்தியிருக்க வேண்டும்",
            "நல்ல கல்வி பதிவு மற்றும் நடத்தை"
          ]
        }
      },
      requirements: {
        title: {
          en: "Award Requirements",
          ta: "விருது தேவைகள்"
        },
        proficiency: {
          title: {
            en: "Required Proficiency Badges",
            ta: "தேவையான தேர்ச்சி பேட்ஜ்கள்"
          },
          badges: {
            en: [
              "World Conservation",
              "Community Development",
              "World Friendship",
              "Public Health",
              "First Aid",
              "Disaster Management",
              "Heritage",
              "Environment Conservation"
            ],
            ta: [
              "உலக பாதுகாப்பு",
              "சமூக மேம்பாடு",
              "உலக நட்புறவு",
              "பொது சுகாதாரம்",
              "முதலுதவி",
              "பேரிடர் மேலாண்மை",
              "பாரம்பரியம்",
              "சுற்றுச்சூழல் பாதுகாப்பு"
            ]
          }
        },
        service: {
          title: {
            en: "Service Requirements",
            ta: "சேவை தேவைகள்"
          },
          content: {
            en: [
              {
                title: "Community Service Project",
                details: "Plan and execute a major community service project",
                hours: "Minimum 40 hours"
              },
              {
                title: "Social Service",
                details: "Regular participation in social service activities",
                hours: "Minimum 100 hours"
              },
              {
                title: "Unit Service",
                details: "Service to unit and helping junior scouts",
                hours: "Minimum 50 hours"
              }
            ],
            ta: [
              {
                title: "சமூக சேவை திட்டம்",
                details: "ஒரு முக்கிய சமூக சேவை திட்டத்தை திட்டமிட்டு செயல்படுத்துதல்",
                hours: "குறைந்தபட்சம் 40 மணிநேரம்"
              },
              {
                title: "சமூக சேவை",
                details: "சமூக சேவை நடவடிக்கைகளில் வழக்கமான பங்கேற்பு",
                hours: "குறைந்தபட்சம் 100 மணிநேரம்"
              },
              {
                title: "அலகு சேவை",
                details: "அலகு சேவை மற்றும் இளைய சாரணர்களுக்கு உதவுதல்",
                hours: "குறைந்தபட்சம் 50 மணிநேரம்"
              }
            ]
          }
        },
        skills: {
          title: {
            en: "Required Skills and Projects",
            ta: "தேவையான திறன்கள் மற்றும் திட்டங்கள்"
          },
          content: {
            en: [
              "Advanced camping and hiking skills",
              "Project planning and execution",
              "Team leadership and management",
              "Environmental conservation project",
              "Cultural heritage documentation",
              "Adventure activities participation",
              "International scouting knowledge",
              "Digital technology proficiency"
            ],
            ta: [
              "மேம்பட்ட முகாமிடல் மற்றும் நடைபயண திறன்கள்",
              "திட்டமிடல் மற்றும் செயல்படுத்துதல்",
              "குழு தலைமைத்துவம் மற்றும் மேலாண்மை",
              "சுற்றுச்சூழல் பாதுகாப்பு திட்டம்",
              "கலாச்சார பாரம்பரிய ஆவணப்படுத்தல்",
              "சாகச நடவடிக்கைகளில் பங்கேற்பு",
              "சர்வதேச சாரணர் அறிவு",
              "டிஜிட்டல் தொழில்நுட்ப தேர்ச்சி"
            ]
          }
        }
      },
      testing: {
        title: {
          en: "Testing Process",
          ta: "சோதனை செயல்முறை"
        },
        stages: {
          en: [
            {
              stage: "Initial Assessment",
              details: "Review of prerequisites and documentation"
            },
            {
              stage: "Written Examination",
              details: "Test of scouting knowledge and problem-solving"
            },
            {
              stage: "Practical Tests",
              details: "Demonstration of scouting skills and leadership"
            },
            {
              stage: "Project Evaluation",
              details: "Assessment of community service project"
            },
            {
              stage: "Interview",
              details: "Personal interview with State/National level officials"
            }
          ],
          ta: [
            {
              stage: "ஆரம்ப மதிப்பீடு",
              details: "முன்தேவைகள் மற்றும் ஆவணங்களின் மதிப்பாய்வு"
            },
            {
              stage: "எழுத்துத் தேர்வு",
              details: "சாரணர் அறிவு மற்றும் சிக்கல் தீர்க்கும் திறன் சோதனை"
            },
            {
              stage: "செயல்முறை சோதனைகள்",
              details: "சாரணர் திறன்கள் மற்றும் தலைமைத்துவ செயல்விளக்கம்"
            },
            {
              stage: "திட்ட மதிப்பீடு",
              details: "சமூக சேவை திட்டத்தின் மதிப்பீடு"
            },
            {
              stage: "நேர்காணல்",
              details: "மாநில/தேசிய அளவிலான அதிகாரிகளுடன் தனிப்பட்ட நேர்காணல்"
            }
          ]
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16 mt-10" >
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-6xl mx-auto"
        >
          {/* Header Section */}
          <div className="text-center mb-16">
            <div className="inline-block p-2 px-4 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
              {isTamil ? 'உயரிய விருது' : 'Highest Award'}
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {translations.title[isTamil ? 'ta' : 'en']}
            </h1>
            <h2 className="text-xl md:text-2xl text-blue-600 font-semibold mb-6">
              {translations.subtitle[isTamil ? 'ta' : 'en']}
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              {translations.description[isTamil ? 'ta' : 'en']}
            </p>
          </div>

          {/* Award Image */}
          <div className="mb-16 rounded-lg overflow-hidden shadow-2xl">
            <img 
              src="/Images/Rashtrapati-Scout-Award.png"
              alt="Rashtrapati Award"
              className="w-20% max-w-md mx-auto "
            />
          </div>

          {/* Eligibility Section */}
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-6">
              <Target className="w-8 h-8 text-blue-600 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.sections.eligibility.title[isTamil ? 'ta' : 'en']}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {translations.sections.eligibility.content[isTamil ? 'ta' : 'en'].map((item, index) => (
                <div key={index} className="flex items-start">
                  <CheckSquare className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Required Badges Section */}
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-6">
              <Medal className="w-8 h-8 text-blue-600 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.sections.requirements.proficiency.title[isTamil ? 'ta' : 'en']}
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {translations.sections.requirements.proficiency.badges[isTamil ? 'ta' : 'en'].map((badge, index) => (
                <div key={index} className="flex items-center bg-gray-50 p-4 rounded-lg">
                  <Award className="w-5 h-5 text-yellow-500 mr-3" />
                  <span className="text-gray-700">{badge}</span>
                </div>
              ))}
            </div>
          </div>

         {/* Service Requirements */}
         <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-6">
              <Users className="w-8 h-8 text-blue-600 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.sections.requirements.service.title[isTamil ? 'ta' : 'en']}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {translations.sections.requirements.service.content[isTamil ? 'ta' : 'en'].map((service, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-700 mb-4">{service.details}</p>
                  <div className="flex items-center text-blue-600">
                    <Clock className="w-4 h-4 mr-2" />
                    <span className="font-medium">{service.hours}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Skills and Projects */}
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-6">
              <BookOpen className="w-8 h-8 text-blue-600 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.sections.requirements.skills.title[isTamil ? 'ta' : 'en']}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {translations.sections.requirements.skills.content[isTamil ? 'ta' : 'en'].map((skill, index) => (
                <div key={index} className="flex items-start">
                  <CheckSquare className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-1" />
                  <span className="text-gray-700">{skill}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Testing Process */}
          <div className="bg-white rounded-xl shadow-md p-8 mb-8">
            <div className="flex items-center mb-8">
              <Calendar className="w-8 h-8 text-blue-600 mr-4" />
              <h2 className="text-2xl font-bold text-gray-900">
                {translations.sections.testing.title[isTamil ? 'ta' : 'en']}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {translations.sections.testing.stages[isTamil ? 'ta' : 'en'].map((stage, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-3">
                      {index + 1}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{stage.stage}</h3>
                  </div>
                  <p className="text-gray-700">{stage.details}</p>
                </div>
              ))}
            </div>
          </div>

        </motion.div>
      </div>
    </div>
  );
};

export default RashtrapatiBadgePage;